import React from "react";

// Wraps the current text selection in **bold** markers on Cmd/Ctrl+B, the
// one formatting shortcut the requirements editor supports. Works on both
// <input> and <textarea> since both expose selectionStart/selectionEnd.
// Deliberately not a full rich-text editor -- just enough to let an admin
// bold a phrase inline without switching to HTML.
export function handleBoldShortcut(
  e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  value: string,
  onChange: (next: string) => void,
) {
  if (!((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b")) return;
  e.preventDefault();
  const target = e.currentTarget;
  const start = target.selectionStart ?? value.length;
  const end = target.selectionEnd ?? value.length;
  const selected = value.slice(start, end);
  const next = `${value.slice(0, start)}**${selected}**${value.slice(end)}`;
  onChange(next);
  // The value update re-renders before the browser would otherwise restore
  // the caret, so put it back explicitly on the next frame.
  requestAnimationFrame(() => {
    target.selectionStart = start + 2;
    target.selectionEnd = end + 2;
  });
}

// Renders the **bold** markers the editor's Cmd/Ctrl+B shortcut writes.
// Used everywhere requirement text is displayed to a visitor (print PDF,
// public program page) -- not a full markdown parser, just this one marker.
export function renderRichText(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**") && part.length > 4 ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
}
